import { useDataProvider, useGetIdentity, type DataProvider } from "ra-core";
import { useCallback, useMemo } from "react";
import { isValidEmail } from "@/utils/email";
import { normalizeUsPhoneToE164 } from "@/utils/phone";

import type { Company, Tag } from "../types";
import { isClientPaymentMethod } from "../../../../supabase/functions/_shared/processingFees.ts";

export type ContactImportSchema = {
  first_name: string;
  last_name: string;
  gender: string;
  title: string;
  company: string;
  email_work: string;
  email_home: string;
  email_other: string;
  phone_work: string;
  phone_home: string;
  phone_other: string;
  background: string;
  avatar: string;
  first_seen: string;
  last_seen: string;
  has_newsletter: string;
  status: string;
  tags: string;
  linkedin_url: string;
  lead_source?: string;
  interested_service?: string;
  address?: string;
  city?: string;
  state_abbr?: string;
  zipcode?: string;
  country?: string;
  /** card, bank or card_or_bank; empty = organization default. */
  payment_method?: string;
};

const parsePaymentMethod = (value?: string) => {
  const normalized = asTrimmedString(value)
    ?.toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (!normalized) return null;
  if (normalized === "ach") return "bank";
  if (isClientPaymentMethod(normalized)) return normalized;
  throw new Error(
    `Invalid payment_method: ${value} (use card, bank or card_or_bank)`,
  );
};

export function useContactImport() {
  const today = new Date().toISOString();
  const user = useGetIdentity();
  const dataProvider = useDataProvider();

  // company cache to avoid creating the same company multiple times and costly roundtrips
  // Cache is dependent of dataProvider, so it's safe to use it as a dependency
  const companiesCache = useMemo(
    () => new Map<string, Company>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataProvider],
  );
  const getCompanies = useCallback(
    async (names: string[]) =>
      fetchRecordsWithCache<Company>(
        "companies",
        companiesCache,
        names,
        (name) => ({
          name,
          created_at: new Date().toISOString(),
          organization_member_id: user?.identity?.id,
        }),
        dataProvider,
      ),
    [companiesCache, user?.identity?.id, dataProvider],
  );

  // Tags cache to avoid creating the same tag multiple times and costly roundtrips
  // Cache is dependent of dataProvider, so it's safe to use it as a dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tagsCache = useMemo(() => new Map<string, Tag>(), [dataProvider]);
  const getTags = useCallback(
    async (names: string[]) =>
      fetchRecordsWithCache<Tag>(
        "tags",
        tagsCache,
        names,
        (name) => ({
          name,
          color: "#f9f9f9",
        }),
        dataProvider,
      ),
    [tagsCache, dataProvider],
  );

  const processBatch = useCallback(
    async (batch: ContactImportSchema[]) => {
      const [companies, tags] = await Promise.all([
        getCompanies(
          batch
            .map((contact) => contact.company?.trim())
            .filter((name) => name),
        ),
        getTags(batch.flatMap((batch) => parseTags(batch.tags))),
      ]);

      await Promise.all(
        batch.map(
          async ({
            first_name,
            last_name,
            gender,
            title,
            email_work,
            email_home,
            email_other,
            phone_work,
            phone_home,
            phone_other,
            lead_source,
            interested_service,
            background,
            first_seen,
            last_seen,
            has_newsletter,
            status,
            company: companyName,
            tags: tagNames,
            linkedin_url,
            address,
            city,
            state_abbr,
            zipcode,
            country,
            payment_method,
          }) => {
            const email_jsonb = [
              { email: email_work, type: "Work" },
              { email: email_home, type: "Home" },
              { email: email_other, type: "Other" },
            ]
              .map((entry) => {
                const trimmedEmail = asTrimmedString(entry.email);
                if (!trimmedEmail) return null;
                if (!isValidEmail(trimmedEmail)) {
                  throw new Error(`Invalid email: ${trimmedEmail}`);
                }
                return { ...entry, email: trimmedEmail };
              })
              .filter((entry) => entry != null);
            const phone_jsonb = [
              { number: phone_work, type: "Work" },
              { number: phone_home, type: "Home" },
              { number: phone_other, type: "Other" },
            ]
              .map((entry) => {
                const trimmedNumber = asTrimmedString(entry.number);
                if (!trimmedNumber) return null;
                const normalizedPhone = normalizeUsPhoneToE164(trimmedNumber);
                if (!normalizedPhone) {
                  throw new Error(`Invalid phone: ${trimmedNumber}`);
                }
                return { ...entry, number: normalizedPhone };
              })
              .filter((entry) => entry != null);
            const normalizedCompanyName = asTrimmedString(companyName);
            const company = normalizedCompanyName
              ? companies.get(normalizedCompanyName)
              : undefined;
            const tagList = parseTags(tagNames)
              .map((name) => tags.get(name))
              .filter((tag): tag is Tag => !!tag);

            return dataProvider.create("contacts", {
              data: {
                first_name,
                last_name,
                gender,
                title,
                email_jsonb,
                phone_jsonb,
                lead_source,
                interested_service,
                background,
                first_seen: first_seen
                  ? new Date(first_seen).toISOString()
                  : today,
                last_seen: last_seen
                  ? new Date(last_seen).toISOString()
                  : today,
                has_newsletter,
                status,
                company_id: company?.id,
                tags: tagList.map((tag) => tag.id),
                organization_member_id: user?.identity?.id,
                linkedin_url,
                address: asTrimmedString(address) || null,
                city: asTrimmedString(city) || null,
                state_abbr: asTrimmedString(state_abbr) || null,
                zipcode: asTrimmedString(zipcode) || null,
                country: asTrimmedString(country) || null,
                payment_method: parsePaymentMethod(payment_method),
              },
            });
          },
        ),
      );
    },
    [dataProvider, getCompanies, getTags, user?.identity?.id, today],
  );

  return processBatch;
}

const fetchRecordsWithCache = async function <T>(
  resource: string,
  cache: Map<string, T>,
  names: string[],
  getCreateData: (name: string) => Partial<T>,
  dataProvider: DataProvider,
) {
  const trimmedNames = [...new Set(names.map((name) => name.trim()))];
  const uncachedRecordNames = trimmedNames.filter((name) => !cache.has(name));

  // check the backend for existing records
  if (uncachedRecordNames.length > 0) {
    const response = await dataProvider.getList(resource, {
      filter: {
        "name@in": `(${uncachedRecordNames
          .map((name) => `"${name}"`)
          .join(",")})`,
      },
      pagination: { page: 1, perPage: trimmedNames.length },
      sort: { field: "id", order: "ASC" },
    });
    for (const record of response.data) {
      cache.set(record.name.trim(), record);
    }
  }

  // create missing records in parallel
  await Promise.all(
    uncachedRecordNames.map(async (name) => {
      if (cache.has(name)) return;
      const response = await dataProvider.create(resource, {
        data: getCreateData(name),
      });
      cache.set(name, response.data);
    }),
  );

  // now all records are in cache, return a map of all records
  return trimmedNames.reduce((acc, name) => {
    acc.set(name, cache.get(name) as T);
    return acc;
  }, new Map<string, T>());
};

const parseTags = (tags: unknown) =>
  asTrimmedString(tags)
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag);

const asTrimmedString = (value: unknown): string => {
  if (value == null) return "";
  return String(value).trim();
};
